"use client";
import { ArchiveTick, Heart } from "iconsax-react";
import { formatNumberWithSuffix } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { useState, useEffect, useCallback } from "react";
import { likeVideoById, saveVideoById } from "@/actions/videos";
import CommentsSheet from "./CommentsSheet";
import ShareDialog from "./ShareDialog";
import SupportCommentDialog from "@/components/customs/Profile/SupportCommentDialog";
import { SupportedLanguage } from "@/lib/shared";
import lang from "@/app/[locale]/(main)/lang.json";

import { useRouter, useSearchParams } from "next/navigation";
import { localizedPath } from "@/lib/localePath";

const VARIANT_CLASSES = {
  /** عمود بجانب الفيديو (شاشات كبيرة — ثابت بالنسبة لحاوية الفيديو) */
  fixedColumn:
    "flex flex-col py-4 gap-x-4 gap-y-2 absolute bottom-4 lg:bottom-0 z-30 start-auto end-4 w-10 lg:end-0 lg:start-full ms-4 items-center",
  /** داخل شريحة الريل (موبايل فقط — يُعرض مع className lg:hidden) */
  reelRail:
    "flex flex-col py-2 gap-y-2 absolute z-30 end-2 bottom-28 w-10 items-center pointer-events-auto text-white",
} as const;

export type CardActionsVariant = keyof typeof VARIANT_CLASSES;

export type CardActionsProps = {
  videoId: string;
  userId: string;
  isLiked?: boolean;
  likes?: number;
  comments?: number;
  shares?: number;
  supportComments?: number;
  save?: number;
  isSaved?: boolean;
  videoURL: string;
  isLoggedIn: boolean;
  locale?: SupportedLanguage;
  variant?: CardActionsVariant;
  /** عند false: ريل غير نشط أثناء التمرير (موبايل) */
  isActive?: boolean;
  /** مثلاً `lg:hidden` للموبايل فقط */
  className?: string;
  onVideoUpdate?: (
    videoId: string,
    updates: Partial<{
      isLiked: boolean;
      noOfLikes: number;
      isSaved: boolean;
      noOfSaves: number;
      noOfComments: number;
      noOfShares: number;
    }>,
  ) => void;
};

export default function CardActions({
  videoId,
  userId,
  likes = 0,
  comments = 0,
  shares = 0,
  supportComments = 0,
  save = 0,
  isLiked = false,
  isSaved = false,
  videoURL,
  isLoggedIn,
  locale = "en",
  variant = "fixedColumn",
  isActive = true,
  className,
  onVideoUpdate,
}: CardActionsProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Local state for optimistic updates
  const [localIsLiked, localIsLikedSet] = useState(isLiked);
  const [localLikes, localLikesSet] = useState(likes);
  const [localIsSaved, localIsSavedSet] = useState(isSaved);
  const [localSaves, localSavesSet] = useState(save);

  // Separate pending states for each action
  const [isLikePending, isLikePendingSet] = useState(false);
  const [isSavePending, isSavePendingSet] = useState(false);

  // Sync local state when props change (when videoId changes)
  useEffect(() => {
    localIsLikedSet(isLiked);
    localLikesSet(likes);
    localIsSavedSet(isSaved);
    localSavesSet(save);
    // Reset pending states when switching videos
    isLikePendingSet(false);
    isSavePendingSet(false);
  }, [videoId, isLiked, likes, isSaved, save]);

  const handleLike = useCallback(async () => {
    if (!isLoggedIn) {
      router.push(
        localizedPath(
          locale,
          `/auth/login?redirect=/?next=like-video-${videoId}`,
        ),
      );
      return;
    }

    if (isLikePending) return; // Prevent multiple clicks

    // Optimistic update
    const newIsLiked = !localIsLiked;
    const newLikes = newIsLiked ? localLikes + 1 : localLikes - 1;

    localIsLikedSet(newIsLiked);
    localLikesSet(newLikes);
    isLikePendingSet(true);

    try {
      const result = await likeVideoById(videoId);

      if (result.status === "error") {
        // Revert optimistic update on error
        localIsLikedSet(!newIsLiked);
        localLikesSet(localLikes);
        console.error("Failed to like video:", result.message);
      } else {
        // Update parent component on success
        onVideoUpdate?.(videoId, {
          isLiked: newIsLiked,
          noOfLikes: newLikes,
        });
      }

      if (searchParams.get("next")) {
        router.replace("?");
      }
    } catch (error) {
      // Revert optimistic update on error
      localIsLikedSet(!newIsLiked);
      localLikesSet(localLikes);
      console.error("Failed to like video:", error);
    } finally {
      isLikePendingSet(false);
    }
  }, [
    isLoggedIn,
    isLikePending,
    localIsLiked,
    localLikes,
    videoId,
    router,
    searchParams,
    onVideoUpdate,
    locale,
  ]);

  const handleSave = useCallback(async () => {
    if (!isLoggedIn) {
      router.push(
        localizedPath(
          locale,
          `/auth/login?redirect=/?next=save-video-${videoId}`,
        ),
      );
      return;
    }

    if (isSavePending) return; // Prevent multiple clicks

    // Optimistic update
    const newIsSaved = !localIsSaved;
    const newSaves = newIsSaved ? localSaves + 1 : localSaves - 1;

    localIsSavedSet(newIsSaved);
    localSavesSet(newSaves);
    isSavePendingSet(true);

    try {
      const result = await saveVideoById(videoId);
      if (result.status === "error") {
        // Revert optimistic update on error
        localIsSavedSet(!newIsSaved);
        localSavesSet(localSaves);
        console.error("Failed to save video:", result.message);
      } else {
        // Update parent component on success
        onVideoUpdate?.(videoId, {
          isSaved: newIsSaved,
          noOfSaves: newSaves,
        });
      }

      if (searchParams.get("next")) {
        router.replace("?");
      }
    } catch (error) {
      // Revert optimistic update on error
      localIsSavedSet(!newIsSaved);
      localSavesSet(localSaves);
      console.error("Failed to save video:", error);
    } finally {
      isSavePendingSet(false);
    }
  }, [
    isLoggedIn,
    isSavePending,
    localIsSaved,
    localSaves,
    videoId,
    router,
    searchParams,
    onVideoUpdate,
    locale,
  ]);

  useEffect(() => {
    const nextAction = searchParams.get("next");

    if (nextAction === `like-video-${videoId}`) {
      handleLike();
    } else if (nextAction === `save-video-${videoId}`) {
      handleSave();
    }
  }, [searchParams, videoId, handleLike, handleSave]);

  const isReelRail = variant === "reelRail";
  const reelTriggerIconClass = isReelRail ? "text-white" : undefined;

  return (
    <div
      className={cn(
        VARIANT_CLASSES[variant],
        !isActive && variant === "reelRail" && "pointer-events-none opacity-40",
        className,
      )}
      aria-hidden={variant === "reelRail" ? !isActive : undefined}
      onMouseDown={
        variant === "reelRail"
          ? (e) => {
              e.stopPropagation();
            }
          : undefined
      }
      onTouchStart={
        variant === "reelRail"
          ? (e) => {
              e.stopPropagation();
            }
          : undefined
      }
    >
      {likes !== undefined && (
        <button
          type="button"
          className="flex flex-col items-center justify-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleLike}
          disabled={isLikePending}
        >
          <span
            className={cn(
              "rounded-full p-2",
              isReelRail ? "" : "bg-gray-200",
            )}
          >
            <Heart
              size={24}
              className={cn(
                "fill-current",
                isLoggedIn && localIsLiked
                  ? isReelRail
                    ? "text-pink-400"
                    : "text-pink-500"
                  : isReelRail && "text-white",
              )}
              variant={
                isReelRail
                  ? "Outline"
                  : isLoggedIn && localIsLiked
                    ? "Bold"
                    : "Outline"
              }
            />
          </span>
          <span
            className={cn(
              localLikes > 0 ? "text-sm" : "text-xs",
              isReelRail && "text-white",
            )}
          >
            {localLikes > 0 ? formatNumberWithSuffix(localLikes) : lang["action-like"][locale]}
          </span>
        </button>
      )}

      {comments !== undefined && (
        <CommentsSheet
          comments={comments}
          videoId={videoId}
          isLoggedIn={isLoggedIn}
          locale={locale}
          reelRailMode={isReelRail}
          triggerIconClassName={reelTriggerIconClass}
          onCommentCountChange={(newCount) => {
            onVideoUpdate?.(videoId, { noOfComments: newCount });
          }}
        />
      )}

      {shares !== undefined && (
        <ShareDialog
          videoId={videoId}
          shares={shares}
          videoURL={videoURL}
          locale={locale}
          reelRailMode={isReelRail}
          triggerIconClassName={reelTriggerIconClass}
          onShareCountChange={(newCount) => {
            onVideoUpdate?.(videoId, { noOfShares: newCount });
          }}
        />
      )}

      {save !== undefined && (
        <button
          type="button"
          className="flex flex-col items-center justify-center gap-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleSave}
          disabled={isSavePending}
        >
          <span
            className={cn(
              "rounded-full p-2",
              isReelRail ? "reel-action-icon-wrap" : "bg-gray-200",
            )}
          >
            <ArchiveTick
              size={24}
              className={cn(
                "fill-current",
                isLoggedIn && localIsSaved
                  ? isReelRail
                    ? "text-amber-300"
                    : "text-yellow-500"
                  : isReelRail && "text-white",
              )}
              variant={
                isReelRail
                  ? "Outline"
                  : isLoggedIn && localIsSaved
                    ? "Bold"
                    : "Outline"
              }
            />
          </span>
          <span
            className={cn(
              localSaves > 0 ? "text-sm" : "text-xs",
              isReelRail && "text-white",
            )}
          >
            {localSaves > 0 ? formatNumberWithSuffix(localSaves) : lang["action-save"][locale]}
          </span>
        </button>
      )}

      {supportComments !== undefined && (
        <SupportCommentDialog
          userId={userId}
          supportComments={supportComments}
          isLoggedIn={isLoggedIn}
          locale={locale}
          reelRailMode={isReelRail}
          triggerIconClassName={reelTriggerIconClass}
        />
      )}
    </div>
  );
}
